/**
 * Tool Call Emitter
 *
 * Simple pub/sub event emitter for listening to specific tool calls from the AI agent.
 * Components can subscribe to tool calls by tool name and receive the input and output.
 * This is an instance-based emitter, not a singleton, to support multiple agents.
 */

export interface ToolCallEvent {
  toolName: string;
  input: any;
  output: any;
  metadata?: {
    toolCallId?: string;
    timestamp?: number;
    [key: string]: any;
  };
}

export type ToolCallHandler = (event: ToolCallEvent) => void;

/**
 * Event emitter for tool calls
 */
export class ToolCallEmitter {
  private listeners: Map<string, Set<ToolCallHandler>> = new Map();

  /**
   * Subscribe to a specific tool call
   * @param {string} toolName - The name of the tool to listen for (e.g., "open_variant_editor")
   * @param {ToolCallHandler} handler - Callback function that receives the tool call event
   * @return {Function} Unsubscribe function
   */
  on(toolName: string, handler: ToolCallHandler): () => void {
    if (!this.listeners.has(toolName)) {
      this.listeners.set(toolName, new Set());
    }
    this.listeners.get(toolName)!.add(handler);

    // Return unsubscribe function
    return () => this.off(toolName, handler);
  }

  /**
   * Unsubscribe from a specific tool call
   * @param {string} toolName - The name of the tool
   * @param {ToolCallHandler} handler - The handler to remove
   */
  off(toolName: string, handler: ToolCallHandler): void {
    const handlers = this.listeners.get(toolName);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.listeners.delete(toolName);
      }
    }
  }

  /**
   * Emit a tool call event to all subscribers
   * @param {ToolCallEvent} event - The tool call event to emit
   */
  emit(event: ToolCallEvent): void {
    const handlers = this.listeners.get(event.toolName);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          console.error(
            `[ToolCallEmitter] Error in handler for tool "${event.toolName}":`,
            error,
          );
        }
      });
    }
  }

  /**
   * Clear all listeners (useful for cleanup in tests)
   */
  clear(): void {
    this.listeners.clear();
  }

  /**
   * Get the number of listeners for a specific tool
   * @param {string} toolName - The name of the tool
   * @return {number} The number of listeners
   */
  listenerCount(toolName: string): number {
    return this.listeners.get(toolName)?.size || 0;
  }

  /**
   * Get all tool names being listened to
   * @return {string[]} Array of tool names
   */
  getToolNames(): string[] {
    return Array.from(this.listeners.keys());
  }
}
